import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, User } from "lucide-react";
import { api, imageSourceFor } from "@/lib/api";
import { SectionLabel } from "@/components/mo";

export default function Stili() {
  const navigate = useNavigate();
  const { data: styles = [] } = useQuery({ queryKey: ["styles"], queryFn: api.getStyles });
  const { data: artists = [] } = useQuery({ queryKey: ["artists"], queryFn: api.getArtists });
  const [openId, setOpenId] = useState(null);
  const openArtist = artists.find((a) => a.id === openId);

  return (
    <div className="mo-rise flex flex-col gap-3 px-5 pt-8 md:px-10">
      <SectionLabel>Mörphösy · Studio</SectionLabel>
      <h1 className="font-display text-4xl font-bold text-mo-ink sm:text-5xl">I Nostri Stili</h1>
      <p className="mb-2 max-w-lg font-body text-sm text-mo-muted">
        Artisti specializzati per ogni stile. Scegli il tuo, o crea qualcosa di unico.
      </p>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {styles.map((s) => {
          const src = imageSourceFor(s);
          return (
            <button
              key={s.id}
              data-testid={`style-card-${s.id}`}
              onClick={() => navigate("/prenota")}
              className="relative h-52 overflow-hidden rounded-xl border border-mo-line bg-mo-surface2 text-left transition-transform hover:-translate-y-0.5"
            >
              {src ? (
                <img src={src} alt={s.name} className="absolute inset-0 h-full w-full object-cover" />
              ) : (
                <div className="absolute inset-0 bg-mo-surface3" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
              <div className="absolute bottom-3 left-3">
                <p className="font-body text-[15px] font-extrabold uppercase tracking-wide text-white">{s.name}</p>
                <span className="mt-0.5 flex items-center gap-0.5 font-body text-xs font-bold text-mo-brand">
                  Scopri <ChevronRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-8">
        <SectionLabel>I Nostri Artisti</SectionLabel>
        <p className="mb-4 mt-1 font-body text-sm text-mo-muted">Ogni stile ha il suo specialista.</p>
        <div className="no-scrollbar flex gap-6 overflow-x-auto pb-2">
          {artists.map((a) => {
            const src = imageSourceFor(a);
            return (
              <button key={a.id} type="button" onClick={() => setOpenId(openId === a.id ? null : a.id)} data-testid={`artist-${a.id}`} className={"flex w-20 flex-shrink-0 flex-col items-center text-center transition-opacity " + (openId && openId !== a.id ? "opacity-50" : "")}>
                <div className="h-18 w-18 rounded-full border-2 border-mo-brand p-0.5" style={{ height: 72, width: 72 }}>
                  {src ? (
                    <img src={src} alt={a.name} className="h-full w-full rounded-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center rounded-full bg-mo-surface3">
                      <User className="h-6 w-6 text-mo-muted" />
                    </div>
                  )}
                </div>
                <p className="mt-2 font-body text-sm font-bold text-mo-ink">{a.name}</p>
                <p className="font-body text-[11px] text-mo-muted">{a.specialty}</p>
              </button>
            );
          })}
        </div>
        {openArtist && (
          <div className="mo-rise mt-4" data-testid="artist-works">
            <p className="mb-3 font-body text-sm font-bold text-mo-ink">Lavori di {openArtist.name}</p>
            {(openArtist.works || []).length === 0 ? (
              <p className="font-body text-xs text-mo-muted">Nessun lavoro pubblicato.</p>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                {openArtist.works.map((w, i) => (
                  <img key={i} src={imageSourceFor(w)} alt={`Lavoro ${i + 1}`} data-testid={`artist-work-${i}`} className="aspect-square w-full rounded-xl border border-mo-line object-cover" />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <button
        onClick={() => navigate("/prenota")}
        data-testid="styles-guide"
        className="mt-6 flex items-center gap-4 rounded-2xl border border-mo-line bg-mo-card p-6 text-left transition-all hover:-translate-y-0.5 hover:border-mo-line2"
      >
        <div className="flex-1">
          <h3 className="font-display text-2xl font-bold text-mo-ink">Il tuo progetto, la nostra guida</h3>
          <p className="mt-1 font-body text-[13px] text-mo-muted">Dall'idea alla cura finale, siamo con te in ogni fase.</p>
        </div>
        <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-mo-brand">
          <ChevronRight className="h-5 w-5 text-mo-onbrand" />
        </span>
      </button>
    </div>
  );
}
